import { resolveApiUrl } from "../assets/GlobalVariables";
import { getUserInfo } from "../services/localStorage";
import { getUserDetails } from "../services/authService";

export const RECENT_ORDERS_HOME_FEED_ENDPOINT = "/getRecentOrdersAll";

const ADMIN_ROLES = new Set(["admin", "super_admin"]);

/** Same precedence as wallet / LDS POST bodies (UserInfo then localStorage sessionID). */
export function resolveHomeFeedSessionId() {
  try {
    const u = getUserInfo();
    const fromUser = u?.sessionID ?? u?.sessionId;
    if (fromUser != null && String(fromUser).trim() !== "") {
      return String(fromUser).trim();
    }
  } catch {
    // ignore
  }
  if (typeof localStorage !== "undefined") {
    const ls = localStorage.getItem("sessionID");
    if (ls != null && String(ls).trim() !== "") {
      return String(ls).trim();
    }
  }
  return "";
}

export function buildRecentOrdersHomeFeedHeaders() {
  const headers = {
    "Content-Type": "application/json; charset=utf-8",
    Accept: "application/json; charset=utf-8",
  };
  const sessionID = resolveHomeFeedSessionId();
  if (sessionID) {
    headers.Authorization = `Bearer ${sessionID}`;
  }
  return headers;
}

export function resolveHomeFeedViewerIsAdmin() {
  const user = getUserDetails() || getUserInfo() || {};
  const role = String(user?.role ?? "").trim().toLowerCase();
  return Boolean(user?.is_admin) || ADMIN_ROLES.has(role);
}

/**
 * Global home feed rows omit owner agent id — only admins may deep-link into arbitrary orders.
 */
export function canOpenRecentFeedOrderDetail(isAdmin = resolveHomeFeedViewerIsAdmin()) {
  return Boolean(isAdmin);
}

export function parseRecentOrdersHomeFeedResponse(res) {
  if (res == null) {
    return {
      ok: false,
      forbidden: false,
      unauthorized: false,
      orders: [],
      error: "null_response",
    };
  }

  const httpStatus = res?.status ?? null;
  let body = res?.data;
  if (typeof body === "string") {
    const trimmed = body.trim();
    if (trimmed) {
      try {
        body = JSON.parse(trimmed);
      } catch {
        body = { message: trimmed };
      }
    }
  }
  const errRaw = body?.error ?? body?.message ?? "";
  const err = String(errRaw || "").toLowerCase();
  const orders = Array.isArray(body?.orders) ? body.orders : [];

  if (orders.length > 0 && body?.success !== false) {
    return { ok: true, forbidden: false, unauthorized: false, orders, error: null };
  }

  if (httpStatus === 403 || err === "forbidden" || err === "forbidden_admin_only") {
    return {
      ok: false,
      forbidden: true,
      unauthorized: false,
      orders: [],
      error: errRaw || "forbidden",
    };
  }

  if (
    httpStatus === 401 ||
    body?.success === false ||
    err === "unauthorized"
  ) {
    return {
      ok: false,
      forbidden: false,
      unauthorized: true,
      orders: [],
      error: errRaw || "unauthorized",
    };
  }

  if (httpStatus != null && httpStatus >= 400) {
    return {
      ok: false,
      forbidden: false,
      unauthorized: false,
      orders: [],
      error: errRaw || `http_${httpStatus}`,
    };
  }

  return { ok: true, forbidden: false, unauthorized: false, orders, error: null };
}

export function traceRecentOrdersHomeFeed(payload) {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.info("[RECENT_ORDERS_HOME_FEED_TRACE]", {
    endpoint: resolveApiUrl(RECENT_ORDERS_HOME_FEED_ENDPOINT),
    ...payload,
  });
}
